import Modal from './lib/Modal.svelte'
import { saveData, savePartialData, getData } from './lib/waves-api'
import type { Platform } from './lib/waves-api'
import { WavesError } from './lib/exceptions'

declare global {
  interface Window {
    waves: Waves
    Wortal: any
    wortalGameID: string | number
  }
}

const GUID = "2c904e1a-861c-4251-8c30-3fd6aa6f89a3";
const WAVES_AUTH_TOKEN_KEY = "waves.authToken";

interface Options {
  baseUrl?: string
  platform?: Platform
  gameId?: number
  authToken?: string
}

class Waves {
  options?: Options
  modal?: Modal
  target?: Element | null

  constructor() {
  }

  init(options: Options) {
    if (this.options) {
      this.options = { ...this.options, ...options }
    } else {
      this.options = { ...options }
    }
    this.options.platform = this.options.platform ?? (window.Wortal?.session?.getPlatform() as Platform)
    this.options.gameId = this.options.gameId ?? window.Wortal?.session?._internalSession?.gameID
    if (!this.options.authToken) {
      const authToken = localStorage.getItem(WAVES_AUTH_TOKEN_KEY);
      if (!!authToken) {
        this.options.authToken = authToken;
      }
    }

    if (!this.options.platform) {
      throw new WavesError("platform is required")
    }

    if (!this.options.gameId) {
      throw new WavesError("gameId is required")
    }
  }

  /**
   * authenticate will check if there is no authToken it will open a dialog
   * to prompt the user to login and save the auth token
   * @param forceDialog if true, will always prompt the user to login
   */
  async authenticate(forceDialog = false) {
    if (!this.options) {
      throw new WavesError("Waves.init() must be called before using authenticate()")
    }
    if (!this.options.authToken || forceDialog) {
      this.options.authToken = await this.getModal().getAuthToken()
      localStorage.setItem(WAVES_AUTH_TOKEN_KEY, this.options.authToken)
    }
  }

  get authToken(): string | undefined {
    return this.options?.authToken
  }

  set authToken(token: string | undefined) {
    if (!this.options) {
      throw new WavesError("Waves.init() must be called before")
    }
    this.options.authToken = token
  }

  async saveData<T = any>(gameData: T) {
    await this.authenticate()
    return await saveData<T>({
      baseUrl: this.options!.baseUrl,
      game_id: this.options!.gameId!,
      auth_token: this.options!.authToken!,
      game_data: gameData,
    })
  }

  async savePartialData<T = any>(gameData: Partial<T>) {
    await this.authenticate()
    return await savePartialData<T>({
      baseUrl: this.options!.baseUrl,
      game_id: this.options!.gameId!,
      auth_token: this.options!.authToken!,
      game_data: gameData,
    })
  }

  async getData<T = any>() {
    await this.authenticate()
    return await getData<T>({
      baseUrl: this.options!.baseUrl,
      game_id: this.options!.gameId!,
      auth_token: this.options!.authToken!,
    })
  }

  protected getTarget(): Element {
    if (this.target) return this.target

    this.target = document.getElementById(GUID)

    if (this.target) return this.target

    this.target = document.createElement('div')
    this.target.id = GUID
    document.body.appendChild(this.target)

    return this.target
  }

  protected getModal(): Modal {
    if (!this.options) {
      throw new WavesError("Waves.init() must be called before getModal()")
    }
    if (!this.modal) {
      this.modal = new Modal({
        target: this.getTarget(),
        props: {
          baseUrl: this.options.baseUrl,
          platform: this.options.platform!,
          gameId: this.options.gameId!,
        }
      })
    } else {
      this.modal.$set({
        baseUrl: this.options.baseUrl,
        platform: this.options.platform!,
        gameId: this.options.gameId!,
      })
    }

    return this.modal
  }
}

const waves = new Waves()
window.waves = waves;
waves.init({
  baseUrl: "https://waves.html5gameportal.dev",
  gameId: Number(window.wortalGameID),
  platform: window.Wortal.session.getPlatform()
})

export default waves
